import { Component, ElementRef, HostBinding, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { NG_VALIDATORS } from '@angular/forms';
import { format, parse, isValid, formatISO } from 'date-fns';
import 'src/app/util/translate-many';
import { map } from 'rxjs/operators';
import { UnsubscribeOnDestroy } from '@app/util/lifecycle-hooks/lifecycle-hooks';
import { DatePickerValidator } from '@app/components/elements/date-picker/date-picker-validator';

import '@vaadin/date-picker';
import { DatePicker, DatePickerI18n } from '@vaadin/date-picker';

@Component({
	// eslint-disable-next-line @angular-eslint/component-selector
	selector: 'vaadin-date-picker',
	template: '',
	styleUrls: ['./date-picker.component.scss'],
	providers: [
		DatePickerValidator,
		{
			provide: NG_VALIDATORS,
			multi: true,
			useExisting: DatePickerValidator,
		},
	],
})
// eslint-disable-next-line rxjs-angular/prefer-composition
export class DatePickerComponent {
	@UnsubscribeOnDestroy
	private readonly subscription = new Subscription();
	
	//region Attributes
	
	@Input()
	@HostBinding()
	public theme: 'align-left' | 'align-center' | 'align-right' | 'small' | 'helper-above-field' = 'align-left';
	
	//endregion
	
	@Input()
	public set name(name: string | null | undefined) {
		this.host.nativeElement.name = name;
	}
	
	@Input()
	public set placeholder(placeholder: string | null | undefined) {
		this.host.nativeElement.placeholder = placeholder;
	}
	
	@Input()
	public set label(label: string | null | undefined) {
		this.host.nativeElement.label = label;
	}
	
	@Input()
	public set helperText(helperText: string | null | undefined) {
		this.host.nativeElement.helperText = helperText;
	}
	
	@Input()
	public set errorMessage(errorMessage: string | null | undefined) {
		this.host.nativeElement.errorMessage = errorMessage;
	}
	
	
	@Input()
	public set min(date: Date | null) {
		this.host.nativeElement.min = date ? formatISO(date, { representation: 'date' }) : null;
	}
	
	@Input()
	public set max(date: Date | null) {
		this.host.nativeElement.max = date ? formatISO(date, { representation: 'date' }) : null;
	}
	
	@Input()
	public set required(required: boolean) {
		this.host.nativeElement.required = required;
	}
	
	@Input()
	public set disabled(disabled: boolean) {
		this.host.nativeElement.disabled = disabled;
	}
	
	@Input()
	public set initialPosition(date: Date | null) {
		this.host.nativeElement.initialPosition = date ? formatISO(date, { representation: 'date' }) : null;
	}
	
	constructor(
		private readonly host: ElementRef<DatePicker>,
		translate: TranslateService,
	) {
		this.subscription.add(
			translate.streamMany([
				'datePicker.week',
				'datePicker.calendar',
				'datePicker.clear',
				'datePicker.today',
				'actions.cancel',
				'datePicker.month.january',
				'datePicker.month.february',
				'datePicker.month.march',
				'datePicker.month.april',
				'datePicker.month.may',
				'datePicker.month.june',
				'datePicker.month.july',
				'datePicker.month.august',
				'datePicker.month.september',
				'datePicker.month.october',
				'datePicker.month.november',
				'datePicker.month.december',
				'datePicker.day.monday',
				'datePicker.day.tuesday',
				'datePicker.day.wednesday',
				'datePicker.day.thursday',
				'datePicker.day.friday',
				'datePicker.day.saturday',
				'datePicker.day.sunday',
				'datePicker.day.monday.short',
				'datePicker.day.tuesday.short',
				'datePicker.day.wednesday.short',
				'datePicker.day.thursday.short',
				'datePicker.day.friday.short',
				'datePicker.day.saturday.short',
				'datePicker.day.sunday.short',
				'datePicker.format',
			]).pipe(
				map((translations): DatePickerI18n => ({
					week: translations['datePicker.week'],
					calendar: translations['datePicker.calendar'],
					clear: translations['datePicker.clear'],
					today: translations['datePicker.today'],
					cancel: translations['actions.cancel'],
					firstDayOfWeek: 1,
					monthNames: [
						translations['datePicker.month.january'],
						translations['datePicker.month.february'],
						translations['datePicker.month.march'],
						translations['datePicker.month.april'],
						translations['datePicker.month.may'],
						translations['datePicker.month.june'],
						translations['datePicker.month.july'],
						translations['datePicker.month.august'],
						translations['datePicker.month.september'],
						translations['datePicker.month.october'],
						translations['datePicker.month.november'],
						translations['datePicker.month.december'],
					],
					weekdays: [
						translations['datePicker.day.monday'],
						translations['datePicker.day.tuesday'],
						translations['datePicker.day.wednesday'],
						translations['datePicker.day.thursday'],
						translations['datePicker.day.friday'],
						translations['datePicker.day.saturday'],
						translations['datePicker.day.sunday'],
					],
					weekdaysShort: [
						translations['datePicker.day.monday.short'],
						translations['datePicker.day.tuesday.short'],
						translations['datePicker.day.wednesday.short'],
						translations['datePicker.day.thursday.short'],
						translations['datePicker.day.friday.short'],
						translations['datePicker.day.saturday.short'],
						translations['datePicker.day.sunday.short'],
					],
					formatTitle: (monthName, fullYear) => `${monthName} ${fullYear}`,
					formatDate: ({ year, month, day }) => format(new Date(year, month, day), translations['datePicker.format']),
					parseDate: (dateString) => {
						const date = parse(dateString, translations['datePicker.format'], Date.now());
						
						if (isValid(date)) {
							return {
								day: date.getDate(),
								month: date.getMonth(),
								year: date.getFullYear(),
							};
						} else {
							return null;
						}
					},
				})),
			).subscribe(i18n => host.nativeElement.i18n = i18n),
		);
	}
}
